/**
 * library.js — Carousel Composer photo library: filename parsing, ordering,
 * search, filters, contact-sheet grouping and selection helpers.
 *
 * Ported from the standalone Carousel Composer artifact with behaviour
 * unchanged. Pure functions; the artifact's globals (lib, slides, famCounts,
 * the light-table filter state) are passed in.
 *
 * Library record: { id, name, url, thumb, w, h, fam, seq, seqStr, variant, source }
 * imgById everywhere is a lookup FUNCTION (id) => record | null — see makeImgById.
 */
import { cellImgId } from './geometry.js';

// "DJI_20260729155620_0471_D.jpg" -> DJI / 0471 ; "DSC09644-Edit.jpg" -> DSC / 9644 / Edit
export function parseName(name) {
  const base = name.replace(/\.[^.]+$/, '');
  const fam = (base.match(/^([A-Za-z]+)/) || ['', '?'])[1].toUpperCase();
  const groups = base.match(/\d+/g) || [];
  let seqStr = groups.length ? groups[groups.length - 1] : '';
  for (let i = groups.length - 1; i >= 0; i--) { if (groups[i].length <= 5) { seqStr = groups[i]; break; } }
  const variant = (base.match(/[-_]([A-Za-z]{2,})(?:$|[-_])/) || ['', ''])[1];
  return {
    fam,
    seq: seqStr ? parseInt(seqStr, 10) : 0,
    seqStr,
    variant: (variant && variant.toUpperCase() !== 'D') ? variant : '',
  };
}

// family, then capture order, with an -Edit sitting right after the frame it came from
export function bySequence(a, b) {
  if (a.fam !== b.fam) return a.fam.localeCompare(b.fam);
  if (a.seq !== b.seq) return a.seq - b.seq;
  if (!!a.variant !== !!b.variant) return a.variant ? 1 : -1;
  return a.name.localeCompare(b.name, undefined, { numeric: true });
}

export function makeImgById(lib) {
  const m = new Map(lib.map((im) => [im.id, im]));
  return (id) => m.get(id) || null;
}

export function shortName(im) {
  return im.fam + im.seqStr + (im.variant ? '-' + im.variant : '');
}

// Tile ratio badge for frames that are not roughly 3:2; '' when none is shown.
export function aspectLabel(im) {
  if (!(Math.abs(im.w / im.h - 1.5) > 0.25)) return '';
  return im.w >= im.h
    ? (im.w / im.h).toFixed(2).replace(/0$/, '') + ':1'
    : '1:' + (im.h / im.w).toFixed(2).replace(/0$/, '');
}

export function computeFamCounts(lib) {
  const m = new Map();
  lib.forEach((im) => { m.set(im.fam, (m.get(im.fam) || 0) + 1); });
  return m;
}

// A family of more than 16 frames splits by hundreds, so "DSC" becomes
// DSC 094xx / 095xx / 096xx — buckets you can actually filter down to.
export function famKey(im, famCounts) {
  if ((famCounts.get(im.fam) || 0) > 16 && im.seqStr.length > 2) {
    return im.fam + ' ' + im.seqStr.slice(0, im.seqStr.length - 2) + 'xx';
  }
  return im.fam;
}

export function norm(s) {
  return s.toLowerCase().replace(/\.[a-z0-9]+$/, '').replace(/[^a-z0-9]/g, '');
}

export function chunks(s) {
  return s.toLowerCase().replace(/\.[a-z0-9]+$/, '').split(/[^a-z0-9]+/).filter(Boolean);
}

// Every chunk of the token must appear in the filename, in order.
export function nameMatches(im, token) {
  const t = norm(token);
  if (!t) return true;
  const n = norm(im.name);
  if (n === t || n.indexOf(t) >= 0 || t.indexOf(n) >= 0) return true;
  const parts = chunks(token);
  if (!parts.length) return false;
  let at = 0;
  for (const part of parts) {
    const i = n.indexOf(part, at);
    if (i < 0) return false;
    at = i + part.length;
  }
  return true;
}

// "9418", "DSC09418", "DJI_0471" and "dji 0471 d" all find the right frame.
export function findImage(lib, token) {
  const t = norm(token);
  if (!t) return null;
  return lib.find((im) => norm(im.name) === t)
    || lib.find((im) => norm(im.name).indexOf(t) >= 0)
    || lib.find((im) => nameMatches(im, token))
    || null;
}

// imgId -> [slide indices]. Pano parts count under their group's photo.
export function usedMap(slides, panos, imgById) {
  const m = new Map();
  slides.forEach((sl, i) => {
    sl.cells.forEach((c, ci) => {
      if (!c) return;
      const id = cellImgId(sl, ci, panos, imgById);
      if (!m.has(id)) m.set(id, []);
      m.get(id).push(i);
    });
  });
  return m;
}

// filters: { q, fams: Set|string[], unused, edits, onlySel }; q is expected trimmed.
export function visibleFilter(lib, filters, used, sel, famCounts) {
  const f = filters || {};
  const fams = f.fams instanceof Set ? f.fams : new Set(f.fams || []);
  let out = lib.slice();
  if (f.q) out = out.filter((im) => nameMatches(im, f.q));
  if (fams.size) out = out.filter((im) => fams.has(famKey(im, famCounts)));
  if (f.unused) out = out.filter((im) => !used.has(im.id));
  if (f.edits) out = out.filter((im) => !!im.variant);
  if (f.onlySel) out = out.filter((im) => sel.indexOf(im.id) >= 0);
  return out;
}

export function hasActiveFilters(filters) {
  const f = filters || {};
  const famN = f.fams instanceof Set ? f.fams.size : (f.fams || []).length;
  return !!(f.q || famN || f.unused || f.edits || f.onlySel);
}

// Chip counts in library order. The artifact shows Unused only when
// 0 < unused < lib.length, Edits when edits > 0, Selected when sel is non-empty.
export function chipCounts(lib, used, famCounts) {
  const counts = new Map();
  lib.forEach((im) => { const k = famKey(im, famCounts); counts.set(k, (counts.get(k) || 0) + 1); });
  return {
    fams: Array.from(counts, ([key, n]) => ({ key, n })),
    unused: lib.filter((im) => !used.has(im.id)).length,
    edits: lib.filter((im) => im.variant).length,
  };
}

// Family bands, plus a hairline wherever the capture sequence jumps —
// that break is where one burst of near-identical frames ends and the next begins.
export function runThreshold(items) {
  const gaps = [];
  for (let i = 1; i < items.length; i++) gaps.push(items[i].seq - items[i - 1].seq);
  if (gaps.length < 4) return Infinity;
  const sorted = gaps.slice().sort((a, b) => a - b);
  const med = sorted[Math.floor(sorted.length / 2)] || 1;
  return Math.max(10, med * 3);
}

// Rows: { t:'tile', im } | { t:'head', key, n } | { t:'run', label }
export function groupRows(list, { searching, famCounts }) {
  const rows = [];
  if (searching) return list.map((im) => ({ t: 'tile', im }));
  const keys = [], byKey = new Map();
  list.forEach((im) => {
    const k = famKey(im, famCounts);
    if (!byKey.has(k)) { byKey.set(k, []); keys.push(k); }
    byKey.get(k).push(im);
  });
  const multi = keys.length > 1;
  keys.forEach((k) => {
    const items = byKey.get(k);
    if (multi) rows.push({ t: 'head', key: k, n: items.length });
    const thr = runThreshold(items);
    for (let i = 0; i < items.length; i++) {
      if (i > 0 && items[i].seq - items[i - 1].seq > thr) {
        let j = i + 1;
        while (j < items.length && items[j].seq - items[j - 1].seq <= thr) j++;
        rows.push({ t: 'run', label: items[i].seqStr + '–' + items[j - 1].seqStr + ' · ' + (j - i) });
      }
      rows.push({ t: 'tile', im: items[i] });
    }
  });
  return rows;
}

export function libInfoText(lib, sel, shown) {
  let t = lib.length ? lib.length + ' photos · ' + sel.length + ' selected' : 'No photos loaded';
  if (lib.length && shown.length !== lib.length) t += ' · ' + shown.length + ' shown';
  const hidden = sel.filter((id) => !shown.some((im) => im.id === id)).length;
  if (hidden) t += ' · ' + hidden + ' selected hidden by the filter';
  return t;
}

/* ---------------- selection (ids in click order) ---------------- */

export function toggleSelection(sel, id) {
  return sel.indexOf(id) >= 0 ? sel.filter((x) => x !== id) : sel.concat(id);
}

// Shift-click over the visible order. Returns null when the anchor or target is not
// visible — the artifact then falls back to a plain toggle.
export function rangeSelection(sel, order, anchorId, id) {
  const a = order.indexOf(anchorId), b = order.indexOf(id);
  if (a < 0 || b < 0) return null;
  const run = a <= b ? order.slice(a, b + 1) : order.slice(b, a + 1).reverse();
  const next = sel.slice();
  run.forEach((x) => { if (next.indexOf(x) < 0) next.push(x); });
  return next;
}

export function selectFamily(sel, visible, key, famCounts) {
  const next = sel.slice();
  visible.forEach((im) => { if (famKey(im, famCounts) === key && next.indexOf(im.id) < 0) next.push(im.id); });
  return next;
}

/* ---------------- the add-one-slide composer ---------------- */

export function roleFor(addStyle, i) {
  if (addStyle === 'stack') return i % 2 ? 'BOTTOM' : 'TOP';
  if (addStyle === 'row') return i % 2 ? 'RIGHT' : 'LEFT';
  if (addStyle === 'hero3') return ['TOP', 'TALL LEFT', 'RIGHT TOP', 'RIGHT BTM'][i % 4];
  if (addStyle === 'pano') return i === 0 ? 'WIDE PHOTO' : '';
  return '';
}

// selImages: the selection mapped to records (null for a missing id).
// panoN: the raw "across N slides" input value (empty falls back to 2).
export function addHintText(addStyle, selImages, panoN, libLength) {
  if (!libLength) return 'load photos first';
  const names = selImages.map((im) => (im ? shortName(im) : '?'));
  if (!names.length) {
    return addStyle === 'pano'
      ? 'select the wide photo you want to pan across'
      : 'nothing selected — adds an empty slide to fill in';
  }
  if (addStyle === 'pano') {
    return names[0] + ' across ' + (panoN || 2) + ' slides, seam-perfect';
  }
  if (addStyle === 'single') {
    return names.length === 1
      ? names[0] + ' on its own slide'
      : 'adds ' + names[0] + ' · Add as singles makes all ' + names.length;
  }
  if (addStyle === 'hero3') {
    const pos = ['on top', 'tall left', 'right top', 'right bottom'];
    const empty = Math.max(0, 4 - names.length);
    return names.slice(0, 4).map((n, i) => n + ' ' + pos[i]).join(', ') +
      (empty ? ' · ' + empty + ' cell' + (empty > 1 ? 's stay' : ' stays') + ' empty' : '') +
      (names.length > 4 ? ' · the fours button uses all ' + names.length : '');
  }
  const role = addStyle === 'stack' ? [' on top', ' below'] : [' on the left', ' on the right'];
  return names.length >= 2
    ? names[0] + role[0] + ', ' + names[1] + role[1] +
      (names.length > 2 ? ' · the pair button uses all ' + names.length : '')
    : names[0] + role[0] + ', second cell stays empty';
}
